import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchSeatingTabData, SeatingTabData } from 'Services/Api/_HumanWritten/SeatingService';
import { appConfigStore } from 'Models/AppConfigStore';
import { useSeatingStore } from 'Stores/SeatingStore';
import useStore from 'Hooks/useStore';
import { useBookingWizardDataStore } from '../../BookingWizardDataStore';
import { patchAllPassengers } from '../../BookingWizardData/BaseBookingWizardData';
import { PassengerDetail, passengerClassPatch } from '../../BookingWizardData/PassengerDetail';

/**
 * Takes off the booking whatever this sailing cannot honour.
 *
 * <p>The ticket step used to clear every seat the moment a sailing or a fare type was touched, so
 * re-picking the fare type a passenger was already in threw their seat away. It cannot decide this:
 * whether a seat still exists, is still free and is still sold in its passenger's fare type is a
 * question about the sailing's own seating, which is what this hook fetches. So the choices are now
 * carried across the ticket step untouched and checked here, against the trip that has to honour
 * them.</p>
 *
 * <p>The fare type is checked first and for the same reason. The seat check reads the passenger's
 * fare type, so one left over from another vessel would condemn a seat that is perfectly good —
 * dropping it instead hands the passenger to the seating step's default-class rule.</p>
 *
 * <p>Run from the fetch rather than from the step, so a booker who changes sailing and goes straight
 * on cannot carry a seat the new vessel does not have as far as the cart.</p>
 */
const useSeatingReconciliation = (
	data: SeatingTabData | undefined,
	tripId: string | undefined,
	leg: 'departing' | 'returning',
) => {
	const { wizardData, setWizardData } = useBookingWizardDataStore();
	/*
		Whoever holds Permission.SeatOutsideFareType may seat a passenger outside their fare type — the
		way an overbooked fare type gets its passengers seated — so for them a seat in another fare type
		is a choice, not a leftover. The server keeps it for the same people and drops it for
		everyone else, which is what this does for everyone else too. A fare-type change on the seating
		step clears a seat that does not suit the new fare type itself, so what survives here was picked
		on purpose.
	*/
	const canSeatOutsideFareType = useStore().can('SeatOutsideFareType');

	React.useEffect(() => {
		// Only ever against this leg's own trip. Checking the departing leg's seats against the
		// returning leg's vessel would clear every one of them.
		if (!wizardData || !data || !tripId || data.tripId !== tripId) {
			return;
		}

		const classIds = new Set(data.passengerClasses.map(x => x.id));
		const seatsById = new Map(data.seats.map(seat => [seat.id, seat]));
		const isReturning = leg === 'returning';
		let changed = false;

		const patched = patchAllPassengers(wizardData, (passenger: PassengerDetail) => {
			const classId = isReturning ? passenger.returningPassengerClassId : passenger.passengerClassId;
			// A trip with no fare types at all is seats-only, not "every fare type is gone".
			const classGone = !!classId && classIds.size > 0 && !classIds.has(classId);

			const seatId = isReturning ? passenger.returningSeatId : passenger.seatId;
			const seat = seatId ? seatsById.get(seatId) : undefined;
			const seatGone =
				!!seatId &&
				(!seat ||
					// Taken by someone else since. This booking's own seats are excluded server-side,
					// so a booker never loses the seat they are already sitting in.
					seat.occupied ||
					// An unclassed seat is pickable from any fare type, a classed one only from its own.
					(!canSeatOutsideFareType &&
						seat.passengerClassIds.length > 0 &&
						!(!classGone && !!classId && seat.passengerClassIds.includes(classId))));

			if (!classGone && !seatGone) {
				return null;
			}

			changed = true;
			return {
				...(classGone ? passengerClassPatch(isReturning ? 'return' : 'departure', undefined, undefined) : {}),
				...(seatGone
					? isReturning
						? {
								returningSeatId: undefined,
								returningSeatLabel: undefined,
								returningSeatAutoAssigned: false,
							}
						: { seatId: undefined, seatLabel: undefined, seatAutoAssigned: false }
					: {}),
			};
		});

		if (changed) {
			setWizardData(patched);
		}
	}, [data, tripId, leg, wizardData, setWizardData, canSeatOutsideFareType]);
};

/**
 * The seating data for one of the booking's sailings.
 *
 * The booking being edited is passed through so its own seats do not read as taken — otherwise a
 * customer could not keep the seat they are already sitting in.
 *
 * <p>Called from the wizard shell, NOT only from the step. The step's visibility is decided from the
 * store, so leaving the fetch to the step alone deadlocks: no data means the step is excluded, an
 * excluded step is never rendered, and an unrendered step never fetches. `SeatingTab` calls this too
 * and shares the query, so the request is still made once.</p>
 */
const useSeatingTabForTrip = (
	tripId: string | undefined,
	mirrorToStore: boolean,
	leg: 'departing' | 'returning',
	// The booking ON THIS SAILING, which on the homeward leg is not the wizard's `bookingToEdit`.
	// Passed in rather than read from the wizard here, so the two legs cannot end up sending the same
	// one — see `useSeatingTabLegs`.
	bookingToEditId: string | undefined,
	journey?: { startStopId?: string; endStopId?: string },
) => {
	const setForTrip = useSeatingStore(state => state.setForTrip);

	const query = useQuery<SeatingTabData, Error>({
		// The segment is part of the key: the same sailing booked between different stops is a
		// different set of fares. The booking is in it too, because it decides which seats come back
		// free, and the two legs pass different ones.
		queryKey: [
			'seating-tab',
			tripId,
			bookingToEditId ?? null,
			journey?.startStopId ?? null,
			journey?.endStopId ?? null,
		],
		queryFn: ({ signal }) => fetchSeatingTabData(tripId!, bookingToEditId, signal, journey),
		// Off means off: a site without the feature must not issue this per trip selection.
		enabled: !!tripId && appConfigStore.passengerClassesEnabled,
		// Seats are contended: another booking can take one while this booker is deciding, and the
		// reservation step re-checks anyway. Keep it short rather than serving a stale map.
		staleTime: 15_000,
	});

	// Mirror into the store so the wizard's step list — built synchronously — can decide whether the
	// step is shown at all and what still needs choosing.
	React.useEffect(() => {
		if (mirrorToStore && tripId && query.data) {
			setForTrip(tripId, query.data);
		}
	}, [mirrorToStore, tripId, query.data, setForTrip]);

	useSeatingReconciliation(query.data, tripId, leg);

	return query;
};

/**
 * Both legs' seating, because a passenger holds a seat on each sailing they are on.
 *
 * <p>Two queries rather than one: the legs are different trips on possibly different vessels, with
 * their own decks, their own classes and their own seats already taken.</p>
 *
 * <p>Both are mirrored into the store, keyed by their own trip, because the step's validator has to
 * ask each leg whether everyone on it has a fare type. While only the departing leg was mirrored it
 * could not see the returning one at all, and a return booking left the step with nobody assigned a
 * fare type home. Mirroring is keyed by trip, so the second entry cannot displace the first.</p>
 *
 * <p><b>Each leg is asked about its own booking.</b> A return journey is two bookings, and the
 * request's `bookingToEditId` is what stops a sailing reporting the booker's own seats as taken.
 * Sending the outbound booking for both legs excluded nothing on the homeward one, so all four of
 * its seats came back `occupied`, the reconciliation above read that as taken by someone else, and
 * the booking arrived at the step with its return seats stripped off.</p>
 */
export function useSeatingTabLegs() {
	const { wizardData } = useBookingWizardDataStore();

	const departing = useSeatingTabForTrip(
		wizardData?.departingJourney?.tripId,
		true,
		'departing',
		wizardData?.bookingToEdit,
		wizardData?.departingJourney,
	);
	const returning = useSeatingTabForTrip(
		wizardData?.returningJourney?.tripId,
		true,
		'returning',
		// Falls back to the outbound booking only where there is no separate return one — a one-way
		// booking, where this query is not made at all, or a leg the wizard holds under one booking.
		wizardData?.returnBookingToEdit ?? wizardData?.bookingToEdit,
		wizardData?.returningJourney,
	);

	return { departing, returning };
}
